/**
 * AI CONTEXT
 * Purpose: shared registry for document-level keyboard listeners.
 * Exposes: add/remove listener with cleanup on unmount.
 * Used by: features that need dynamic hotkeys.
 * Importance: centralizes key listener lifecycle management.
 * Refactor: use functional state updates to avoid stale listener arrays.
 */
import { noop } from "lodash"
import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react"

type Callback = (this: Document, ev: KeyboardEvent) => unknown

export enum KeyListenerType {
  keydown = "keydown",
}

interface KeyListenerOption {
  callback: Callback
  type: KeyListenerType
}

interface KeyListenerContextType {
  addKeyListener: (keyListener: KeyListenerOption) => void
  removeKeyListener: (keyListener: KeyListenerOption) => void
}

export const KeyListenerContext = createContext<KeyListenerContextType>({
  addKeyListener: noop,
  removeKeyListener: noop,
})

const KeyListenerContextProvider = ({ children }: { children: ReactNode }) => {
  const [keyListeners, setKeyListeners] = useState<KeyListenerOption[]>([])

  const addKeyListener = useCallback(
    (keyListener: KeyListenerOption) => {
      const { type, callback } = keyListener
      document.addEventListener(type, callback)
      setKeyListeners([...keyListeners, keyListener])
    },
    [keyListeners]
  )

  const removeKeyListener = useCallback(
    (keyListener: KeyListenerOption) => {
      const { type, callback } = keyListener
      document.removeEventListener(type, callback)
      setKeyListeners(
        keyListeners.filter((listener) => listener !== keyListener)
      )
    },
    [keyListeners]
  )

  useEffect(() => {
    return () => {
      keyListeners.forEach(({ type, callback }) => {
        document.removeEventListener(type, callback)
      })
    }
  }, [keyListeners])

  const defaultValue = {
    addKeyListener,
    removeKeyListener,
  }

  return (
    <KeyListenerContext.Provider value={defaultValue}>
      {children}
    </KeyListenerContext.Provider>
  )
}

export default KeyListenerContextProvider
